/**
 * AssetDetails Component (GraphQL version)
 * Displays asset defect and repair history using GraphQL API via services-px
 */

import React, { useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import {
	Container,
	Paper,
	Typography,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	Divider,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon, FlagOutlined } from '@mui/icons-material';
import { Loading } from '@gpstrackit/gtc-react-controls';
import { setAssetInfoFromNavigation, getAssetInspection, reset } from './redux/slice';
import OpenPendingDefects from './components/openPendingDefects';
import ClosedIgnoredDefects from './components/closedIgnoredDefects';
import './assetDetails.scss';

const AssetDetails = () => {
	const { assetId } = useParams();
	const navigate = useNavigate();
	const location = useLocation();
	const dispatch = useDispatch();
	const { messages } = useIntl();

	const { assetInfo, inspectionDetails, loadingInspection } = useSelector(
		(state) => state.assetDetailsRedux
	);

	// Get asset data and return path from navigation state
	const assetDataFromNav = location.state?.assetData;
	const returnPath = location.state?.returnPath;
	const returnTitle = location.state?.returnTitle || messages['asset_details_back'] || 'Back';

	useEffect(() => {
		if (assetId) {
			// Always fetch the last inspection to get telemetry data (ODOMETER, ENGINE HOURS, etc.)
			// companyId is inferred by the backend from the user's auth token
			dispatch(getAssetInspection({ assetId }));
		} else if (assetDataFromNav) {
			// Fallback: use the asset data passed via navigation if we don't have assetId
			dispatch(setAssetInfoFromNavigation(assetDataFromNav));
		}
	}, [assetDataFromNav, assetId, dispatch]);

	// Separate cleanup effect that only runs on unmount
	useEffect(() => {
		return () => {
			dispatch(reset());
		};
	}, []);

	const handleBack = () => {
		// Use browser history when navigated here normally (preserves history stack)
		// Fall back to navigate() for deep-links/bookmarks
		if (location.state?.returnPath) {
			window.history.back();
		} else if (returnPath) {
			navigate(returnPath);
		} else {
			window.history.back();
		}
	};

	// Build asset details array - start with asset number and equipment type,
	// then add all dynamic inspection details from the API
	const assetDetails = [
		{
			label: messages['asset_details_asset_number'] || 'ASSET NUMBER',
			content: assetInfo.assetNumber || 'N/A',
			icon: assetInfo.reconciledAssetCategory === 'user' ? true : false,
			iconTooltip: messages['asset_details_user_created'] || 'User Created Asset',
		},
		{
			label: messages['asset_details_equipment_type'] || 'EQUIPMENT TYPE',
			content: assetInfo.equipmentType || 'N/A',
		},
		// Add all dynamic inspection details from the API
		...(inspectionDetails || []).map((detail) => ({
			label: detail.label?.toUpperCase() || detail.category?.toUpperCase() || 'UNKNOWN',
			content: detail.content,
		})),
	];

	if (loadingInspection) {
		return (
			<Container maxWidth={false} className="container-view">
				<Paper elevation={0} style={{ padding: 24 }}>
					<Loading />
				</Paper>
			</Container>
		);
	}

	return (
		<Container maxWidth={false} className="container-view asset-details-page">
			<Paper elevation={0} style={{ padding: 24 }}>
				{/* Header with back link */}
				<div style={{ marginBottom: 24 }}>
					<Typography
						variant="body2"
						component="a"
						onClick={handleBack}
						style={{
							cursor: 'pointer',
							color: '#1976d2',
							textDecoration: 'none',
							fontSize: '12px',
							display: 'block',
							marginBottom: 9,
						}}
					>
						&lt; {messages['asset_details_back'] || 'Back'}</Typography>
					<Typography variant="h4" style={{ fontWeight: 600, fontSize: '28px' }}>
						{messages['asset_details_title'] || 'Asset Defect and Repair History'}
					</Typography>
				</div>

				{/* Asset Details Card */}
				<Paper
					variant="outlined"
					style={{
						padding: 24,
						marginBottom: 24,
						borderRadius: 2,
						backgroundColor: '#fff',
					}}
				>
					<div style={{ display: 'flex', flexWrap: 'wrap' }}>
						{assetDetails.map((detail, index) => (
							<div
								key={index}
								style={{
									flex: '0 1 calc(33.33%)',
									display: 'flex',
									flexDirection: 'column',
								}}
							>
								{/* Label and Content in horizontal row */}
								<div
									style={{
										display: 'flex',
										alignItems: 'flex-end',
										margin: '8px 0',
									}}
								>
									<div
										style={{
											flex: '0 0 44%',
											fontSize: '14px',
											color: '#666',
											lineHeight: '19px',
										}}
									>
										{detail.label}
									</div>
									<div
										style={{
											flex: '0 0 50%',
											fontSize: '14px',
											fontWeight: 600,
											lineHeight: '19px',
											wordBreak: 'break-word',
											display: 'flex',
											alignItems: 'center',
										}}
									>
										{detail.content}
										{detail.icon && (
											<FlagOutlined
												style={{
													fontSize: 16,
													color: '#666',
													marginLeft: 4,
												}}
												titleAccess={detail.iconTooltip}
											/>
										)}
									</div>
								</div>
								{/* Divider - hide for last 3 items */}
								{index < assetDetails.length - 3 && (
									<div style={{ width: '95%' }}>
										<Divider style={{ borderColor: '#ebebeb' }} />
									</div>
								)}
							</div>
						))}
					</div>
				</Paper>

				{/* Open and Pending Defects Accordion */}
				<Accordion defaultExpanded style={{ marginBottom: 16 }}>
					<AccordionSummary
						expandIcon={<ExpandMoreIcon />}
						aria-controls="open-pending-content"
						id="open-pending-header"
					>
						<Typography variant="h6" style={{ fontWeight: 600 }}>
							{messages['asset_details_open_pending_defects'] || 'Open and pending defects'}
						</Typography>
					</AccordionSummary>
					<AccordionDetails>
						{assetId && (
							<OpenPendingDefects assetId={assetId} />
						)}
					</AccordionDetails>
				</Accordion>

				{/* Closed / Ignored Defects Accordion */}
				<Accordion style={{ marginBottom: 16 }}>
					<AccordionSummary
						expandIcon={<ExpandMoreIcon />}
						aria-controls="closed-ignored-content"
						id="closed-ignored-header"
					>
						<Typography variant="h6" style={{ fontWeight: 600 }}>
							{messages['asset_details_closed_ignored_defects'] || 'Closed / Ignored defects'}
						</Typography>
					</AccordionSummary>
					<AccordionDetails>
						{assetId && (
							<ClosedIgnoredDefects assetId={assetId} />
						)}
					</AccordionDetails>
				</Accordion>
			</Paper>
		</Container>
	);
};

export default AssetDetails;
